/**
 * Unified Realtime AI Hook
 * Provides a consistent API while using WebRTC under the hood
 * 
 * This is a compatibility layer that:
 * - Uses WebRTC for better performance
 * - Maintains the same API as the old useVoiceMode
 * - Allows AIInspect.jsx to work without changes
 */

import { useState, useRef, useCallback } from 'react';
import { useWebRTCRealtime } from './useWebRTCRealtime';
import { useNotifications } from '../contexts/NotificationContext';

export function useRealtimeAI() {
  const { showError, showSuccess } = useNotifications();
  
  // WebRTC hook
  const {
    isConnected,
    isConnecting,
    isListening,
    userTranscript,
    aiTranscript,
    threadId,              // ✅ PHASE 1: Get threadId from WebRTC
    conversationId,
    connect,
    disconnect,
    sendEvent,
    sendTextMessage,       // ✅ PHASE 3: Get text message sending
    toggleMute,
    clearThread            // ✅ PHASE 1: Get clearThread method
  } = useWebRTCRealtime();
  
  // State for compatibility
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isRealtimeMode, setIsRealtimeMode] = useState(false);
  const [transcript, setTranscript] = useState('');
  
  // Refs
  const addMessageRef = useRef(null);
  const isDisconnectingRef = useRef(false); // ✅ FIX: Prevent race conditions
  
  /**
   * Start Voice Mode (legacy - maps to realtime mode)
   */
  const startVoiceMode = useCallback(async (addMessage) => {
    // ✅ FIX: Don't start if we're in the middle of disconnecting
    if (isDisconnectingRef.current) {
      console.warn('⚠️ Cannot start voice mode while disconnecting');
      return;
    }
    
    // ✅ FIX: If already connected, disconnect first
    if (isConnected) {
      console.log('⚠️ Already connected, disconnecting first...');
      await disconnect(addMessage);
      await new Promise(resolve => setTimeout(resolve, 500)); // Wait for cleanup
    }
    
    console.log('🎤 Starting voice mode (WebRTC)...');
    addMessageRef.current = addMessage;
    
    try {
      await connect(addMessage);
      setIsVoiceMode(true);
      setIsRealtimeMode(true);
      
      if (addMessage) {
        addMessage({
          type: 'system',
          content: 'Voice mode activated. Speak naturally with the AI assistant.',
          timestamp: new Date()
        });
      }
      
      showSuccess('Voice mode activated');
    } catch (error) {
      console.error('❌ Failed to start voice mode:', error);
      showError('Failed to start voice mode');
      throw error;
    }
  }, [connect, disconnect, isConnected, showSuccess, showError]);
  
  /**
   * Start Realtime Mode (WebRTC)
   * ✅ PHASE 1 FIX: Accept threadId parameter to continue existing conversations
   */
  const startRealtimeMode = useCallback(async (addMessage, callback, existingThreadId = null) => {
    // ✅ FIX: Don't start if we're in the middle of disconnecting
    if (isDisconnectingRef.current) {
      console.warn('⚠️ Cannot start realtime mode while disconnecting');
      return;
    }
    
    // ✅ FIX: If already connected, disconnect first
    if (isConnected) {
      console.log('⚠️ Already connected, disconnecting first...');
      await disconnect(addMessage);
      await new Promise(resolve => setTimeout(resolve, 500)); // Wait for cleanup
    }
    
    console.log('🎤 Starting realtime mode (WebRTC)...');
    if (existingThreadId) {
      console.log(`💬 Continuing existing thread: ${existingThreadId.substring(0, 8)}...`);
    }
    addMessageRef.current = addMessage;
    
    try {
      // ✅ PHASE 1 FIX: Pass existingThreadId to connect
      await connect(addMessage, existingThreadId);
      setIsRealtimeMode(true);
      setIsVoiceMode(true);
      
      // Don't add system message - just show toast notification
      showSuccess('AI conversation started');
      
      // Call callback if provided (for inspection mode)
      if (callback && typeof callback === 'function') {
        callback();
      }
    } catch (error) {
      console.error('❌ Failed to start realtime mode:', error);
      showError('Failed to start AI conversation');
      throw error;
    }
  }, [connect, showSuccess, showError]);
  
  /**
   * Toggle Listening (mute/unmute microphone)
   */
  const toggleListening = useCallback(() => {
    console.log('🎤 Toggling microphone...');
    const newState = toggleMute();
    
    if (newState) {
      showSuccess('Microphone unmuted');
    } else {
      showSuccess('Microphone muted');
    }
    
    return newState;
  }, [toggleMute, showSuccess]);
  
  /**
   * End Voice Mode
   * ✅ PHASE 3 FIX: Don't disconnect WebRTC - just switch UI to text mode
   * This allows seamless switching between voice and text within the same session
   */
  const endVoiceMode = useCallback(async (addMessage) => {
    console.log('🛑 Ending voice mode (keeping connection alive)...');
    
    try {
      // ✅ Just toggle UI state - don't disconnect!
      setIsVoiceMode(false);
      // Keep isRealtimeMode TRUE so text messages still use WebRTC
      // setIsRealtimeMode(false);  // ❌ DON'T DO THIS!
      setTranscript('');
      
      if (addMessage) {
        addMessage({
          type: 'system',
          content: 'Voice mode ended. You can continue chatting with text.',
          timestamp: new Date()
        });
      }
      
      showSuccess('Voice mode ended - text mode active');
    } catch (error) {
      console.error('❌ Failed to end voice mode:', error);
      showError('Failed to end voice mode');
    }
  }, [showSuccess, showError]);
  
  /**
   * End Realtime Mode
   * ✅ PHASE 3 FIX: This should ONLY be called when truly closing the conversation
   * (e.g., clicking "X" to close AI Inspector or "New Chat")
   * NOT when switching between voice and text modalities!
   */
  const endRealtimeMode = useCallback(async (addMessage) => {
    // ✅ FIX: Set disconnecting flag to prevent new connections
    if (isDisconnectingRef.current) {
      console.warn('⚠️ Already disconnecting...');
      return;
    }
    
    console.log('🛑 Ending realtime mode (full disconnect)...');
    isDisconnectingRef.current = true;
    
    try {
      await disconnect(addMessage);
      setIsRealtimeMode(false);
      setIsVoiceMode(false);
      setTranscript('');
      
      if (addMessage) {
        addMessage({
          type: 'system',
          content: 'AI conversation ended.',
          timestamp: new Date()
        });
      }
      
      showSuccess('AI conversation ended');
    } catch (error) {
      console.error('❌ Failed to end realtime mode:', error);
      showError('Failed to end AI conversation');
    } finally {
      // ✅ FIX: Reset disconnecting flag after cleanup
      setTimeout(() => {
        isDisconnectingRef.current = false;
      }, 1000);
    }
  }, [disconnect, showSuccess, showError]);
  
  /**
   * Handle Realtime Image Upload
   */
  const handleRealtimeImageUpload = useCallback(async (imageData) => {
    console.log('📷 Handling realtime image upload...');
    
    try {
      if (!isConnected) {
        throw new Error('Not connected to realtime session');
      }
      
      // Send image via data channel
      sendEvent({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{
            type: 'input_image',
            image: imageData
          }]
        }
      });
      
      // Trigger response
      sendEvent({
        type: 'response.create'
      });
      
      showSuccess('Image sent to AI');
    } catch (error) {
      console.error('❌ Failed to upload image:', error);
      showError('Failed to send image');
      throw error;
    }
  }, [isConnected, sendEvent, showSuccess, showError]);
  
  // Return the same API as useVoiceMode for compatibility
  return {
    // State
    isVoiceMode,
    isRealtimeMode,
    isListening: isConnected && isListening,
    transcript: aiTranscript || transcript,
    userTranscript,
    aiTranscript,
    isConnecting,
    threadId,              // ✅ PHASE 1: Expose threadId
    conversationId,        // Alias for backward compatibility
    
    // Methods
    startVoiceMode,
    startRealtimeMode,
    toggleListening,
    endVoiceMode,
    endRealtimeMode,
    handleRealtimeImageUpload,
    sendTextMessage,       // ✅ PHASE 3: Expose text message sending
    clearThread            // ✅ PHASE 1: Expose clearThread method
  };
}

